/**
 * 3-blok — foydalanuvchilar uchun yo'riqnomalar (ЧТЗ 4.2.11.1, 2.4-band).
 *
 * Jismoniy va yuridik shaxslar uchun alohida toifalar; PDF hujjatlar
 * (yo'riqnoma, reglament) va YouTube/Vimeo video-yo'riqnomalar. Ro'yxatni
 * kontent-menejer superadmin panelida boshqaradi: yuklash, nomi/tavsifini
 * tahrirlash, saralash, faollashtirish/o'chirish (`portal.GuideDocument`).
 *
 * Video modal oynada ochiladi, avtomatik ijro va ovoz yo'q, subtitr
 * (mavjud bo'lsa) yoqilgan holda — WCAG 1.2.
 */
import { useMemo, useState } from "react";

import { portalApi } from "@/shared/api/endpoints";
import type { PortalGuide } from "@/shared/api/types";
import { guidesFallback } from "@/shared/data/landing";
import { useI18n } from "@/shared/i18n";
import { Icon } from "@/shared/ui/Icon";
import { Modal } from "@/shared/ui/Modal";

import { SplitHead } from "./SplitHead";
import { usePortalGuides } from "./publicData";

type Audience = "individual" | "legal_entity";

interface GuideView {
  id: string;
  audience: Audience;
  format: "pdf" | "video" | "link";
  title: string;
  text: string;
  fileUrl: string;
  fileSize: number;
  embedUrl: string;
  videoUrl: string;
  serverId?: number;
}

function fromServer(item: PortalGuide): GuideView {
  return {
    id: String(item.id),
    audience: item.audience === "legal_entity" ? "legal_entity" : "individual",
    format: item.doc_format,
    title: item.title,
    text: item.description,
    fileUrl: item.file_url,
    fileSize: item.file_size,
    embedUrl: item.embed_url,
    videoUrl: item.video_url,
    serverId: item.id,
  };
}

function size(bytes: number): string {
  if (!bytes) return "";
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

const TABS: Array<{ key: Audience; label: string; icon: "user" | "briefcase" }> = [
  { key: "individual", label: "Jismoniy shaxslar uchun", icon: "user" },
  { key: "legal_entity", label: "Yuridik shaxslar uchun", icon: "briefcase" },
];

/** `embedded` — «Biz haqimizda» sahifasining ichida (o'z sarlavhasi va konteynersiz) ko'rsatiladi. */
export function GuidesSection({ embedded = false }: { embedded?: boolean }) {
  const { t } = useI18n();
  const guides = usePortalGuides();
  const [audience, setAudience] = useState<Audience>("individual");
  const [video, setVideo] = useState<GuideView | null>(null);

  const items: GuideView[] = useMemo(() => {
    if (guides.data?.length) {
      return guides.data.filter((item) => item.audience !== "employee").map(fromServer);
    }
    return guidesFallback.map((item) => ({
      id: item.id,
      audience: item.audience,
      format: item.format,
      title: item.title,
      text: item.text,
      fileUrl: "",
      fileSize: 0,
      embedUrl: "",
      videoUrl: "",
    }));
  }, [guides.data]);

  const visible = items.filter((item) => item.audience === audience);
  const counts = {
    individual: items.filter((item) => item.audience === "individual").length,
    legal_entity: items.filter((item) => item.audience === "legal_entity").length,
  };

  function download(item: GuideView) {
    if (item.serverId) void portalApi.guideDownload(item.serverId).catch(() => undefined);
  }

  return (
    <section
      className={embedded ? "about-block" : "section"}
      id={embedded ? "yoriqnomalar" : "guides"}
      aria-labelledby="guides-title"
      data-reveal={embedded ? true : undefined}
    >
      <div className={embedded ? undefined : "container"}>
        {embedded ? (
          <div className="about-block-head">
            <h3 id="guides-title">Foydalanuvchilar uchun yo'riqnomalar</h3>
            <p className="hint">Xizmatlardan foydalanish bo'yicha PDF va video yo'riqnomalar.</p>
          </div>
        ) : (
          <SplitHead
            scene="guides"
            side="right"
            eyebrow={t("nav.guides")}
            title="Foydalanuvchilar uchun yo'riqnomalar"
            titleId="guides-title"
            lead="Xizmatlardan foydalanish bo'yicha PDF va video yo'riqnomalar."
          />
        )}

        <div className="guides" data-reveal={embedded ? undefined : true}>
          <div className="guides-tabs" role="tablist" aria-label="Foydalanuvchi toifasi">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`guides-tab-${tab.key}`}
                aria-selected={audience === tab.key}
                aria-controls="guides-panel"
                className={audience === tab.key ? "is-active" : undefined}
                onClick={() => setAudience(tab.key)}
              >
                <Icon name={tab.icon} size={18} />
                {tab.label}
                <span className="num">{counts[tab.key]}</span>
              </button>
            ))}
          </div>

          <ul
            className="guides-grid"
            id="guides-panel"
            role="tabpanel"
            aria-labelledby={`guides-tab-${audience}`}
          >
            {visible.map((item) => {
              const isVideo = item.format === "video";
              const available = isVideo ? Boolean(item.embedUrl || item.videoUrl) : Boolean(item.fileUrl);
              return (
                <li className="guide-card" key={item.id}>
                  <span className={`guide-format is-${item.format}`} aria-hidden="true">
                    <Icon name={isVideo ? "play" : "file"} size={22} />
                  </span>
                  <div className="guide-body">
                    <span className="badge badge-plain">{isVideo ? "Video" : "PDF"}</span>
                    <h3>{item.title}</h3>
                    {item.text ? <p>{item.text}</p> : null}
                  </div>
                  <div className="guide-actions">
                    {!available ? (
                      <span className="badge" aria-label="Hujjat tez orada joylanadi">
                        <Icon name="clock" size={12} />
                        Tez orada
                      </span>
                    ) : isVideo ? (
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => setVideo(item)}>
                        <Icon name="play" size={14} />
                        Ko'rish
                      </button>
                    ) : (
                      <a
                        className="btn btn-outline btn-sm"
                        href={item.fileUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={() => download(item)}
                      >
                        <Icon name="download" size={14} />
                        Yuklab olish {size(item.fileSize) ? <span className="muted">· {size(item.fileSize)}</span> : null}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
            {visible.length === 0 ? (
              <li className="guide-empty">Bu toifa uchun yo'riqnomalar hali joylanmagan.</li>
            ) : null}
          </ul>
        </div>
      </div>

      <Modal open={Boolean(video)} title={video?.title ?? ""} onClose={() => setVideo(null)} size="lg">
        {video ? (
          <>
            {video.embedUrl ? (
              <div className="video-frame">
                <iframe
                  src={video.embedUrl}
                  title={video.title}
                  allow="encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            ) : (
              <p>
                <a href={video.videoUrl} target="_blank" rel="noreferrer noopener">
                  Videoni yangi oynada ochish
                </a>
              </p>
            )}
            {video.text ? <p className="video-description">{video.text}</p> : null}
            <p className="hint">Video avtomatik ijro etilmaydi; subtitrlarni pleyer sozlamalaridan yoqish mumkin.</p>
          </>
        ) : null}
      </Modal>
    </section>
  );
}
